import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, InputGroup, Spinner } from 'react-bootstrap';
import FormPage from '../components/FormPage';
import FormCard from '../components/FormCard';
import FormField from '../components/FormField';
import FormActions from '../components/FormActions';
import { userService, Role } from '../services/userService';
import { useTranslation } from '../contexts/LocaleContext';
import { ROUTES } from '../constants';
import { generatePassword, validatePassword } from '../utils/passwordUtils';

const UserForm: React.FC = () => {
  const t = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState<number>(1);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (id) loadUser();
  }, [id]);

  const loadRoles = async () => {
    try {
      const list = await userService.getRoles();
      setRoles(list);
      if (list.length > 0 && roleId === 1 && !list.some((r) => r.id === 1)) {
        setRoleId(list[0].id);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  const loadUser = async () => {
    if (!id) return;
    try {
      const user = await userService.getUser(parseInt(id, 10));
      setUserName(user.userName);
      setRoleId(user.role?.id ?? 1);
    } catch (error) {
      console.error('Error loading user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePassword = () => {
    setPassword(generatePassword(12));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit && !id) return;

    if (!isEdit && !password.trim()) {
      alert(t('validation.password.minLength'));
      return;
    }
    if (!isEdit || password.trim()) {
      const validation = validatePassword(password);
      if (!validation.valid) {
        alert(validation.messageKey ? t(validation.messageKey) : t('errors.invalidPassword'));
        return;
      }
    }

    try {
      if (isEdit) {
        const data: { userName?: string; password?: string; roleId?: number } = {
          userName,
          roleId,
        };
        if (password.trim()) data.password = password;
        await userService.updateUser(parseInt(id!, 10), data);
      } else {
        await userService.createUser({ userName, password, roleId });
      }
      navigate(ROUTES.ADMIN_USERS);
    } catch (error) {
      console.error(isEdit ? 'Error updating user' : 'Error creating user', error);
      alert(isEdit ? t('errors.updateUser') : t('errors.createUser'));
    }
  };

  if (isEdit && loading) {
    return (
      <FormPage title={t('users')} backFallbackRoute={ROUTES.ADMIN_USERS}>
        <div className="d-flex justify-content-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      </FormPage>
    );
  }

  return (
    <FormPage
      title={isEdit ? `${t('manage.edit')} — ${t('users')}` : t('formField.createUser')}
      backFallbackRoute={ROUTES.ADMIN_USERS}
    >
      <Form onSubmit={handleSubmit}>
        <FormCard>
          <FormField label={t('formField.name')} htmlFor="userName">
            <Form.Control
              type="text"
              id="userName"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              required
            />
          </FormField>
          <FormField
            label={
              <>
                {t('formField.password')}
                {isEdit && ` (${t('formField.passwordLeaveEmpty')})`}
              </>
            }
            htmlFor="password"
          >
            <InputGroup>
              <Form.Control
                type="text"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('formField.passwordPlaceholder')}
                required={!isEdit}
              />
              <Button variant="outline-secondary" type="button" onClick={handleGeneratePassword}>
                {t('formField.generatePassword')}
              </Button>
            </InputGroup>
            <Form.Text className="text-muted">{t('formField.passwordRules')}</Form.Text>
          </FormField>
          <FormField label={t('formField.roleId')} htmlFor="roleId">
            <Form.Select
              id="roleId"
              value={roleId}
              onChange={(e) => setRoleId(parseInt(e.target.value, 10) || 1)}
              required
              disabled={rolesLoading}
            >
              {rolesLoading && (
                <option value={1}>{t('Loading...')}</option>
              )}
              {!rolesLoading && roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Form.Select>
          </FormField>
          <FormActions
            submitLabel={t('manage.save')}
            backTo={ROUTES.ADMIN_USERS}
            backLabel={t('users')}
          />
        </FormCard>
      </Form>
    </FormPage>
  );
};

export default UserForm;
